import { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Stack,
} from "@mui/material";
import { TextFields } from "@mui/icons-material";
import { toastAlert } from "../../../../utils/AlertContext";
import { useTitleStyles } from "../../../../hooks/panelEditor/useGetTitle";
import { useDocumentStyles } from "../../../../hooks/panelEditor/usePutTitle";
import FontControls from "../globalStylesSection/components/FontControls";
import PositionControls from "../globalStylesSection/components/PositionControls";
import { AVAILABLE_FONTS, POSITIONS} from "../globalStylesSection/constants/styleDefaults";

const Header = ({ token, idDocument, openColorModal }) => {
  const { stylesTitle, refresh } = useTitleStyles(
    token,
    idDocument
  );
  const { updateTitleStyles, loading: savingStyles } = useDocumentStyles(
    token,
    idDocument
  );

  const [localTitleStyles, setLocalTitleStyles] = useState({});

  useEffect(() => {
    if (stylesTitle) {
      setLocalTitleStyles({
        font: stylesTitle.font,
        color: stylesTitle.color,
        fontSize: stylesTitle.font_size,
        position: stylesTitle.position,
        text: stylesTitle.text || "",
        fontWeight: stylesTitle.font_weight,
      });
    }
  }, [stylesTitle]);

  const handleTitleChange = (updates) => {
    setLocalTitleStyles((prev) => ({ ...prev, ...updates }));
  };

  const handleSaveTitle = async () => {
    try {
      const result = await updateTitleStyles(localTitleStyles);
      if (result.success) {
        refresh();
        toastAlert("Encabezado guardado exitosamente", "success");
      } else {
        toastAlert(result.error || "Error al guardar el encabezado", "error");
      }
    } catch (error) {
      toastAlert("Error al guardar el encabezado", "error");
    }
  };

  return (
    <Card sx={{ borderRadius: "16px", bgcolor: "#ffffff" }}>
      <CardContent sx={{ p: 4 }}>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <TextFields sx={{ fontSize: 20, color: "#1976d2" }} />
          <Typography variant="h6" fontWeight="bold">
            Encabezado
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Configura el encabezado del documento: texto, fuente, tamaño, color y
          posición.
        </Typography>
        <Stack spacing={3}>
          <TextField
            fullWidth
            label="Visualiza el encabezado"
            variant="outlined"
            value={localTitleStyles.text || ""}
            onChange={(e) => handleTitleChange({ text: e.target.value })}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "16px",
                "& fieldset": {
                  borderColor: "#d1d5db",
                  borderWidth: "1px",
                },
              },
            }}
            InputProps={{
              style: {
                fontFamily: localTitleStyles.font,
                fontSize: `${localTitleStyles.fontSize}px`,
                color: localTitleStyles.color,
                fontWeight: localTitleStyles.fontWeight,
                textAlign: localTitleStyles.position,
              },
            }}
          />

          <FontControls
            font={localTitleStyles.font}
            fontSize={localTitleStyles.fontSize}
            fontWeight={localTitleStyles.fontWeight}
            color={localTitleStyles.color}
            fonts={AVAILABLE_FONTS}
            onFontChange={(value) => handleTitleChange({ font: value })}
            onFontSizeChange={(value) => handleTitleChange({ fontSize: value })}
            onFontWeightChange={(value) =>
              handleTitleChange({ fontWeight: value })
            }
            onOpenColorPicker={() =>
              openColorModal(
                "title",
                localTitleStyles.color || "#000000",
                (newColor) => handleTitleChange({ color: newColor }),
                "Seleccionar color de texto"
              )
            }
          />

          <PositionControls
            value={localTitleStyles.position}
            onChange={(value) => handleTitleChange({ position: value })}
            label="Posición del título"
            positions={POSITIONS}
          />

          <Grid container justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={handleSaveTitle}
              disabled={savingStyles}
              sx={{
                mt: 2,
                bgcolor: "#1976d2",
                color: "white",
                borderRadius: "50px",
                px: 4,
                py: 1.5,
                textTransform: "capitalize",
                "&:hover": { bgcolor: "#1565c0" },
                "&:disabled": {
                  bgcolor: "#e0e0e0",
                  color: "#9e9e9e",
                },
              }}
            >
              {savingStyles ? "Guardando..." : "Guardar Cambios"}
            </Button>
          </Grid>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default Header;
